import { TimeSpans } from '../../constants/utility/time-spans.constants';

/**
 * Single shared poller for `/api/systems/status`.
 *
 * Every consumer (the landing page tiles, each micro-frontend route) used to run its own
 * 30s fetch loop, so one open tab polled the endpoint several times per interval. They now
 * subscribe to this store instead: one request per interval no matter how many subscribers,
 * and the latest result is available immediately to a component that mounts later.
 *
 * Polling runs only while there is at least one subscriber and the tab is visible; a tab
 * coming back to the foreground refreshes straight away.
 */

const POLL_INTERVAL_MS = TimeSpans.ThirtySeconds;

// A subscriber arriving within this window of the last fetch reuses it instead of refetching
// (e.g. landing page unmounting as a micro-frontend route mounts).
const FRESH_FOR_MS = TimeSpans.FiveSeconds;

const STATUS_URL = '/api/systems/status';

/**
 * @typedef {{ isActive: boolean, maintenanceMessage?: string|null, estimatedReturnUtc?: string|null }} SystemStatus
 * @typedef {{ systems: Record<string, SystemStatus>|null, isLoaded: boolean, error: Error|null }} StatusSnapshot
 */

/** @type {StatusSnapshot} */
let snapshot = { systems: null, isLoaded: false, error: null };

/** @type {Set<(snapshot: StatusSnapshot) => void>} */
const listeners = new Set();

let timerId = null;
let inFlight = null;
let lastFetchedAt = 0;

export const getSnapshot = () => snapshot;

const publish = (next) => {
    if (
        next.systems === snapshot.systems &&
        next.isLoaded === snapshot.isLoaded &&
        next.error === snapshot.error
    ) {
        return;
    }
    snapshot = next;
    listeners.forEach((listener) => listener(snapshot));
};

/**
 * Keeps the previous object for every system whose status is unchanged, so a consumer
 * watching one system doesn't see a new reference (and re-run effects) because a different
 * system changed. Returns the previous map itself when nothing changed at all.
 */
const mergeSystems = (previous, incoming) => {
    if (!previous) return incoming;

    let changed = Object.keys(previous).length !== Object.keys(incoming).length;
    const merged = {};
    for (const [key, value] of Object.entries(incoming)) {
        if (previous[key] && JSON.stringify(previous[key]) === JSON.stringify(value)) {
            merged[key] = previous[key];
        } else {
            merged[key] = value;
            changed = true;
        }
    }
    return changed ? merged : previous;
};

/** Fetches once; concurrent callers share the same request. */
export const refreshSystemStatuses = () => {
    if (inFlight) return inFlight;

    inFlight = fetch(STATUS_URL, { credentials: 'include' })
        .then((response) => {
            if (!response.ok) throw new Error(`Failed to fetch system status: ${response.status}`);
            return response.json();
        })
        .then((data) => {
            if (!data?.systems) throw new Error('System status response had no systems.');
            publish({
                systems: mergeSystems(snapshot.systems, data.systems),
                isLoaded: true,
                error: null,
            });
        })
        .catch((error) => {
            // Keep the last good statuses on a transient failure so a live micro-frontend
            // isn't unmounted by one bad poll. If nothing has ever loaded, `systems` stays
            // null and consumers fail closed (treated as offline), as before.
            publish({ ...snapshot, isLoaded: true, error });
        })
        .finally(() => {
            lastFetchedAt = Date.now();
            inFlight = null;
        });

    return inFlight;
};

const isTabHidden = () => typeof document !== 'undefined' && document.hidden;

const stopTimer = () => {
    if (timerId !== null) {
        clearInterval(timerId);
        timerId = null;
    }
};

const startTimer = () => {
    if (timerId !== null || isTabHidden()) return;
    timerId = setInterval(refreshSystemStatuses, POLL_INTERVAL_MS);
};

const onVisibilityChange = () => {
    if (listeners.size === 0) return;
    if (isTabHidden()) {
        stopTimer();
    } else {
        refreshSystemStatuses();
        startTimer();
    }
};

const start = () => {
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibilityChange);
    }
    if (Date.now() - lastFetchedAt > FRESH_FOR_MS) {
        refreshSystemStatuses();
    }
    startTimer();
};

const stop = () => {
    if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
    }
    stopTimer();
};

/**
 * Subscribes to status updates; polling starts with the first subscriber and stops with the last.
 * @param {(snapshot: StatusSnapshot) => void} listener
 * @returns {() => void} unsubscribe
 */
export const subscribe = (listener) => {
    listeners.add(listener);
    if (listeners.size === 1) start();

    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stop();
    };
};
